import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'

import { guardRoute } from '../auth/guard.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { parseOneOf, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

// Who the viewer can cover, and how. A session and no role (spec §6): the menu keeps officials
// and coaches away, and whoever has nobody to cover sees the empty state.

/** What Stripe appends to the return URL after a payment or a card change. */
export const REDIRECT_STATUSES = ['succeeded', 'processing', 'failed'] as const
export type RedirectStatus = (typeof REDIRECT_STATUSES)[number]

export interface SubscriptionsSearch {
  /** The person a payment just went through for, until the webhook makes them covered. */
  readonly activating: number | null
  // Stripe's own name. Read once on arrival, then dropped from the URL with its siblings.
  readonly redirect_status: RedirectStatus | null
}

const DEFAULT_SUBSCRIPTIONS_SEARCH: SubscriptionsSearch = {
  activating: null,
  redirect_status: null,
}

export const subscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PATHS.subscriptions,
  validateSearch: (search: Record<string, unknown>): SubscriptionsSearch => ({
    activating: parsePositiveInt(search.activating),
    redirect_status: parseOneOf(REDIRECT_STATUSES, search.redirect_status),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_SUBSCRIPTIONS_SEARCH)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/subscriptions.tsx')),
})

export const subscriptionsLink = {
  to: NAV_PATHS.subscriptions,
  search: DEFAULT_SUBSCRIPTIONS_SEARCH,
} as const
